import React, { useState, useEffect, useMemo, useRef } from "react";
import DeckGL from "@deck.gl/react";
import { Map } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { TripsLayer } from "@deck.gl/geo-layers";
import { PolygonLayer, ScatterplotLayer } from "@deck.gl/layers";
import { AmbientLight, PointLight, LightingEffect } from "@deck.gl/core";
import { DatePicker, Slider } from "antd";
import dayjs from "dayjs";
import PositionIcon from "../assets/icons/Vector.svg";
import VelocityWidget from "./VelocityWidget";
import TraffixLogo from "../assets/Traffixcolored.svg";
import "../styles/GenovaTrips.css";
import { fetchAnimatedVehicles } from "../APIs/apiService";

// Vehicle data will be loaded from the new animated vehicles GeoJSON
const VEHICLE_DATA_URL = "/animated_vehicles.geojson";

// Configuration constants from animated_vehicles.html
const CLASS_WIDTHS = { Auto: 2.0, Furgoni: 3.0, Pesanti: 5.0 };
const DEST_COLORS = {
  "Imbarchi Passeggeri": [230, 230, 230],
  "Imbarchi merci": [0, 180, 255],
  Levante: [0, 200, 120],
  "Passo Nuovo": [255, 160, 40],
  Ponente: [160, 100, 255],
};

// Destination points (centroids) from animated_vehicles.html
const DEST_POINTS = [
  {
    dest: "Imbarchi Passeggeri",
    position: [8.9149, 44.40767],
    color: [230, 230, 230],
  },
  { dest: "Passo Nuovo", position: [8.90365, 44.40935], color: [255, 160, 40] },
  { dest: "Ponente", position: [8.90709, 44.40808], color: [160, 100, 255] },
  {
    dest: "Imbarchi merci",
    position: [8.90988, 44.40747],
    color: [0, 180, 255],
  },
  { dest: "Levante", position: [8.91516, 44.41407], color: [0, 200, 120] },
];

// Animation constants from animated_vehicles.html
const START_AT = 291.123261128934;
const CENTROID_RADIUS_M = 10.0;
const TRAIL = 2;

const INITIAL_VIEW_STATE = {
  longitude: 8.91,
  latitude: 44.41,
  zoom: 16,
  pitch: 75,
  bearing: 0,
};

// Zoom controls component for DeckGL
function DeckGLZoomControls({ deckRef }) {
  const handleZoomIn = () => {
    if (deckRef.current && deckRef.current.deck) {
      const currentViewState = deckRef.current.deck.viewState;
      deckRef.current.deck.setProps({
        initialViewState: {
          ...currentViewState,
          zoom: Math.min(currentViewState.zoom + 1, 22),
        },
      });
    }
  };

  const handleZoomOut = () => {
    if (deckRef.current && deckRef.current.deck) {
      const currentViewState = deckRef.current.deck.viewState;
      deckRef.current.deck.setProps({
        initialViewState: {
          ...currentViewState,
          zoom: Math.max(currentViewState.zoom - 1, 1),
        },
      });
    }
  };

  const baseStyle = {
    backgroundColor: "white",
    color: "#333",
    border: "1px solid rgba(0,0,0,0.2)",
    borderRadius: "6px",
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <button onClick={handleZoomIn} title="Zoom in" style={baseStyle}>
        +
      </button>
      <button onClick={handleZoomOut} title="Zoom out" style={baseStyle}>
        -
      </button>
    </div>
  );
}

// Current location button for DeckGL
function DeckGLCurrentLocationButton({ deckRef }) {
  const handleClick = () => {
    if (!navigator.geolocation) {
      alert("Geolocalizzazione non supportata");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (deckRef.current && deckRef.current.deck) {
          const currentViewState = deckRef.current.deck.viewState;
          deckRef.current.deck.setProps({
            initialViewState: {
              ...currentViewState,
              longitude,
              latitude,
              zoom: 18,
            },
          });
        }
      },
      (error) => {
        console.error("Error getting location:", error);
        alert("Impossibile ottenere la tua posizione.");
      }
    );
  };

  const buttonStyle = {
    backgroundColor: "white",
    color: "#2d3b2d",
    border: "1px solid rgba(0,0,0,0.2)",
    borderRadius: "10px",
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
  };

  return (
    <button
      onClick={handleClick}
      title="Vai alla mia posizione"
      style={buttonStyle}
    >
      <img
        src={PositionIcon}
        alt="Current location"
        style={{ width: 20, height: 20 }}
      />
    </button>
  );
}

// Map controls wrapper for DeckGL
function DeckGLMapControls({ deckRef }) {
  return (
    <div
      style={{
        position: "absolute",
        right: 16,
        bottom: 170,
        zIndex: 1200,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        alignItems: "flex-end",
      }}
    >
      <DeckGLCurrentLocationButton deckRef={deckRef} />
      <DeckGLZoomControls deckRef={deckRef} />
    </div>
  );
}

const TripComponent = () => {
  const [time, setTime] = useState(START_AT);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [timeOfDay, setTimeOfDay] = useState(12); // Hour of the day (0-23)
  const [isVelocityExpanded, setIsVelocityExpanded] = useState(false);

  // Vehicle data from animated_vehicles.html
  const [vehicleData, setVehicleData] = useState([]);

  // Speed filter state (keeping for compatibility with VelocityWidget)
  const [speedFilters, setSpeedFilters] = useState({
    all: true,
    high: false,
    medium: false,
    low: false,
  });

  // Handle filter changes from VelocityWidget (currently unused, but kept for future implementation)
  const handleFilterChange = (filters) => {
    setSpeedFilters(filters);
  };

  // Handle date change
  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  // Handle time slider change
  const handleTimeChange = (value) => {
    setTimeOfDay(value);
  };

  // Format time for display
  const formatTime = (hour) => {
    return `${hour.toString().padStart(2, "0")}:00`;
  };

  // Load vehicle data from GeoJSON using centralized API service
  useEffect(() => {
    fetchAnimatedVehicles()
      .then((geojson) => {
        // Convert GeoJSON features to vehicle data format
        const vehicles = geojson.features.map((feature) => ({
          id: feature.properties.id,
          path: feature.geometry.coordinates,
          timestamps: feature.properties.timestamps,
          class: feature.properties.class,
          od: feature.properties.od,
          dest: feature.properties.dest,
        }));
        console.log(`Loaded ${vehicles.length} vehicles from GeoJSON`);
        setVehicleData(vehicles);
      })
      .catch((error) => console.error("Error loading vehicle data:", error));
  }, []);

  // Calculate max timestamp for animation loop
  const tMax = useMemo(() => {
    if (vehicleData.length === 0) return 120;
    return Math.max(
      ...vehicleData.map((d) => d.timestamps[d.timestamps.length - 1] || 0)
    );
  }, [vehicleData]);

  // Animation loop using setInterval (simpler and prevents issues)
  useEffect(() => {
    if (vehicleData.length === 0) return;

    const loopLength = tMax + 5;

    const interval = setInterval(() => {
      setTime((prevTime) => {
        // Calculate next time value
        const nextTime = prevTime + 0.1;
        // Loop back if we exceed the max
        return nextTime >= loopLength ? START_AT : nextTime;
      });
    }, 100); // Update every 100ms

    return () => clearInterval(interval);
  }, [tMax, vehicleData.length]);

  const lightingEffect = useMemo(() => {
    const ambientLight = new AmbientLight({
      color: [255, 255, 255],
      intensity: 1.0,
    });

    const pointLight = new PointLight({
      color: [255, 255, 255],
      intensity: 2.0,
      position: [-122.45, 37.75, 8000],
    });

    return new LightingEffect({ ambientLight, pointLight });
  }, []);

  // Calculate velocity statistics from vehicle data
  const velocityStats = useMemo(() => {
    if (vehicleData.length === 0) {
      return { total: 0, high: 0, medium: 0, low: 0, average: 0 };
    }

    // For now, we'll use placeholder stats since vehicles don't have speed property
    // In the HTML, vehicles are colored by destination, not speed
    return {
      total: vehicleData.length,
      high: 0,
      medium: 0,
      low: 0,
      average: 0,
    };
  }, [vehicleData]);

  console.log("Loaded vehicles:", vehicleData.length);
  console.log("Animation tMax:", tMax);

  // Helper to get color by destination (from HTML)
  const getColorByDest = (d) => {
    const c = DEST_COLORS[d.dest];
    return Array.isArray(c) ? c : [230, 230, 230];
  };

  // Helper to get width by vehicle class (from HTML)
  const getWidthByClass = (d) => {
    const w = CLASS_WIDTHS[d.class];
    return typeof w === "number" && isFinite(w) ? w : 2.0;
  };

  // Create layers - time dependency will cause recreation only when time changes
  // But that's necessary for the TripsLayer animation
  const layers = [
    // Main TripsLayer for all vehicles
    new TripsLayer({
      id: "animated-vehicles",
      data: vehicleData,
      getPath: (d) => d.path,
      getTimestamps: (d) => d.timestamps,
      getColor: getColorByDest,
      getWidth: getWidthByClass,
      widthUnits: "pixels",
      widthMinPixels: 1,
      trailLength: TRAIL,
      opacity: 0.95,
      capRounded: true,
      jointRounded: true,
      pickable: true,
      currentTime: time,
      shadowEnabled: false,
    }),
    // Destination centroids ScatterplotLayer
    new ScatterplotLayer({
      id: "dest-centroids",
      data: DEST_POINTS,
      getPosition: (d) => d.position,
      getFillColor: (d) => d.color,
      getRadius: CENTROID_RADIUS_M,
      radiusUnits: "meters",
      stroked: false,
      filled: true,
      opacity: 0.95,
      pickable: true,
    }),
    // Buildings layer
    new PolygonLayer({
      id: "buildings",
      data: "/buildings_genova.json",
      extruded: true,
      wireframe: false,
      opacity: 0.3,
      getPolygon: (f) => f.polygon,
      getElevation: (f) => f.height,
      getFillColor: [74, 80, 87],
      material: {
        ambient: 1,
        diffuse: 0.6,
        shininess: 32,
        specularColor: [60, 64, 70],
      },
    }),
  ];

  // Tooltip function memoized to prevent recreating on every render
  const getTooltip = useMemo(() => {
    return ({ object, layer }) => {
      if (!object) return null;
      if (layer && layer.id === "dest-centroids") {
        return `Destinazione: ${object.dest}`;
      }
      return `ID: ${object.id}\nOD: ${object.od}\nTipo: ${object.class}\nDest: ${object.dest}`;
    };
  }, []);

  // Use a ref to store DeckGL instance for programmatic control
  const deckRef = useRef(null);

  // Legend data based on vehicle destinations and classes
  const legendData = useMemo(() => {
    const destinationCounts = {};
    const classCounts = {};

    vehicleData.forEach((vehicle) => {
      const dest = vehicle.dest || "Unknown";
      const vClass = vehicle.class || "Unknown";

      destinationCounts[dest] = (destinationCounts[dest] || 0) + 1;
      classCounts[vClass] = (classCounts[vClass] || 0) + 1;
    });

    return { destinationCounts, classCounts };
  }, [vehicleData]);

  return (
    <div style={{ height: "100vh", width: "100vw", position: "relative" }}>
      {/* Betwin Logo */}
      <div className="traffix-logo-container">
          <img src={TraffixLogo} alt="Traffix Logo" className="betwin-logo-img" />
     
      </div>

      {/* Date and Time Controls */}
      <div className="trips-controls-container">
        <div className="trips-controls-card">
          <div className="trips-control-item">
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              format="DD/MM/YYYY"
              className="trips-date-picker"
              allowClear={false}
            />
          </div>
          <div className="trips-control-item trips-time-slider">
            <Slider
              min={0}
              max={23}
              value={timeOfDay}
              onChange={handleTimeChange}
              marks={{
                0: { label: "00:00" },
                6: { label: "06:00" },
                12: { label: "12:00" },
                18: { label: "18:00" },
                23: { label: "23:00" },
              }}
              tooltip={{ formatter: formatTime }}
              styles={{
                track: {
                  background: "transparent",
                },
                tracks: {
                  background: "transparent",
                },
              }}
            />
          </div>

          {/* Velocity Widget */}
        </div>
      </div>

      {/* Legend - inspired by animated_vehicles.html */}
      <div
        style={{
          position: "absolute",
          right: 8,
          top: 110,
          background: "rgba(20,20,24,.85)",
          padding: "8px 10px",
          borderRadius: "6px",
          font: "12px/1.4 system-ui,Arial",
          color: "#ddd",
          boxShadow: "0 1px 3px rgba(0,0,0,.35)",
          maxWidth: "280px",
          zIndex: 1000,
        }}
      >
        {/* Class widths legend */}
        <div style={{ marginBottom: "6px" }}>
          <strong>Spessore per classe</strong>
          {Object.entries(CLASS_WIDTHS).map(([className, width]) => (
            <div
              key={className}
              style={{
                display: "flex",
                alignItems: "center",
                margin: "3px 0",
                gap: "8px",
              }}
            >
              <span
                style={{
                  width: "22px",
                  height: `${Math.max(4, width)}px`,
                  borderRadius: "3px",
                  background: "#e6e6e6",
                }}
              ></span>
              <span>
                {className} ({width}px) -{" "}
                {legendData.classCounts[className] || 0}
              </span>
            </div>
          ))}
        </div>

        {/* Destination colors legend */}
        <div style={{ marginBottom: "6px" }}>
          <strong>Colore per destinazione</strong>
          {Object.entries(DEST_COLORS).map(([destName, [r, g, b]]) => (
            <div
              key={destName}
              style={{
                display: "flex",
                alignItems: "center",
                margin: "3px 0",
                gap: "8px",
              }}
            >
              <span
                style={{
                  width: "22px",
                  height: "6px",
                  borderRadius: "3px",
                  background: `rgb(${r},${g},${b})`,
                  border: "1px solid rgba(255,255,255,.15)",
                }}
              ></span>
              <span>
                {destName} - {legendData.destinationCounts[destName] || 0}
              </span>
            </div>
          ))}
        </div>
      </div>

      <DeckGL
        ref={deckRef}
        effects={[lightingEffect]}
        initialViewState={INITIAL_VIEW_STATE}
        controller={true}
        layers={layers}
        style={{ height: "100%", width: "100%" }}
        getTooltip={getTooltip}
      >
        <Map
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
          mapStyle={`https://basemaps.cartocdn.com/gl/dark-matter-nolabels-gl-style/style.json?key=${process.env.REACT_APP_CARTO_KEY}`}
          style={{ height: "100%", width: "100%" }}
        />
      </DeckGL>

      <DeckGLMapControls deckRef={deckRef} />
    </div>
  );
};

export default TripComponent;
